import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

const siteUrl = 'https://pipicinema.com';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center text-foreground-300">
              <i className="ri-emotion-sad-line text-5xl"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">影片未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查找的影片不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: 10,
      ratingCount: Math.floor(Math.random() * 50000) + 10000,
    },
    url: `${siteUrl}/detail/${movie.id}`,
  };

  return (
    <>
      <PageMeta
        title={`${movie.title} - 在线观看 | 屁屁影院`}
        description={`${movie.title}：${movie.description.slice(0, 120)}。导演${movie.director}，主演${movie.actors.slice(0, 3).join('、')}等。在屁屁影院免费在线观看高清版本。`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.title}高清,${movie.title}免费观看,屁屁影院,${movie.categoryLabel}`}
        canonicalUrl={`${siteUrl}/detail/${movie.id}`}
        ogImage={movie.poster}
        ogType="video.movie"
        schema={articleSchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: movie.categoryLabel, href: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />
        </div>

        {/* Movie Detail */}
        <section className="px-4 md:px-6 pb-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Poster */}
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} - 屁屁影院高清海报`}
                    title={`${movie.title} 海报`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-sm font-medium">
                    <i className="ri-star-fill text-xs"></i>
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-500">{movie.categoryLabel}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 font-medium min-w-[60px] whitespace-nowrap">导演</span>
                    <span className="text-sm text-foreground-950">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 font-medium min-w-[60px] whitespace-nowrap">主演</span>
                    <span className="text-sm text-foreground-950">{movie.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 font-medium min-w-[60px] whitespace-nowrap">更新</span>
                    <span className="text-sm text-foreground-600">{movie.updateDate}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-secondary-100 text-secondary-800 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-secondary-100 text-secondary-800 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Synopsis */}
        <section className="px-4 md:px-6 pb-10">
          <div className="max-w-5xl mx-auto">
            <article>
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4" id="synopsis">
                <a href="#synopsis">剧情简介</a>
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
              </div>
            </article>
          </div>
        </section>

        {/* Related */}
        {relatedMovies.length > 0 && (
          <section className="px-4 md:px-6 pb-14">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}